import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentType } from '../../types';
import { PatientTypeEnum } from '../../types';

export type PatientType = PatientTypeEnum | '';

export interface PatientDetails {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes?: string;
  id?: string;
  external_resource_id?: string;
  birth_date?: string;
  paymentType?: 'insurance' | 'myself' | null;
  insuranceDetails?: {
    provider?: string;
    policyHolderName?: string;
    policyHolderDob?: string;
    memberId?: string;
  };
}

export const createEmptyPatientDetails = (): PatientDetails => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: '',
  birth_date: '',
  paymentType: null,
  insuranceDetails: {
    provider: '',
    policyHolderName: '',
    policyHolderDob: '',
    memberId: '',
  },
});

interface AppointmentState {
  treatment: AppointmentType | null;
  patientType: PatientType;
  selectedSlot: string | null;
  patientDetails: PatientDetails;
  selectedPmsOperatoryId?: string | null;
  selectedDurationMinutes?: number | null;
  selectedOperatories?: string[];
  request_id: string | null;
  patient_request_id: string | null;
  requestable_type: string | null;
}

const initialState: AppointmentState = {
  treatment: null,
  patientType: '',
  selectedSlot: null,
  patientDetails: createEmptyPatientDetails(),
  selectedPmsOperatoryId: null,
  selectedDurationMinutes: null,
  selectedOperatories: [],
  request_id: null,
  patient_request_id: null,
  requestable_type: null,
};

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setTreatment(state, action: PayloadAction<AppointmentType>) {
      state.treatment = action.payload;
      // Reset downstream selections when treatment changes
      state.selectedSlot = null;
      state.selectedPmsOperatoryId = null;
      // Recompute duration if patient type already chosen
      if (state.patientType && action.payload) {
        state.selectedDurationMinutes =
          state.patientType === 'new'
            ? action.payload.newPatientMinutes
            : action.payload.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setRequestMetadata: (state, action: PayloadAction<{ request_id: string; requestable_id: string; requestable_type: string }>) => {
      state.request_id = action.payload.request_id;
      state.patient_request_id = action.payload.requestable_id;
      state.requestable_type = action.payload.requestable_type;
    },
    setPatientType(state, action: PayloadAction<PatientType>) {
      state.patientType = action.payload;
      if (state.treatment) {
        state.selectedDurationMinutes =
          action.payload === 'new'
            ? state.treatment.newPatientMinutes
            : state.treatment.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setSelectedSlot(state, action: PayloadAction<string | null>) {
      state.selectedSlot = action.payload;
    },
    setSelectedPmsOperatoryId(state, action: PayloadAction<string | null>) {
      state.selectedPmsOperatoryId = action.payload;
    },
    setSelectedOperatories(state, action: PayloadAction<string[]>) {
      state.selectedOperatories = action.payload
    },
    setPatientDetails(state, action: PayloadAction<PatientDetails>) {
      state.patientDetails = action.payload;
    },
    resetAppointment(state) {
      state.treatment = null;
      state.patientType = '';
      state.selectedSlot = null;
      state.patientDetails = createEmptyPatientDetails();
      state.selectedPmsOperatoryId = null;
      state.selectedDurationMinutes = null;
      state.selectedOperatories = [];
      state.request_id = null;
      state.patient_request_id = null;
      state.requestable_type = null;
    },
  },
});

export const {
  setTreatment,
  setPatientType,
  setSelectedSlot,
  setSelectedPmsOperatoryId,
  setSelectedOperatories,
  setPatientDetails,
  setRequestMetadata,
  resetAppointment,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;





